import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Boxes, LayoutDashboard, GitMerge, Gauge, Sparkles, Upload, Moon, Sun, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/mro";

const NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard, id: "nav-overview" },
  { to: "/standardization", label: "Standardization", icon: GitMerge, id: "nav-standardization" },
  { to: "/recommendations", label: "Safety Stock", icon: Gauge, id: "nav-recommendations" },
  { to: "/copilot", label: "Copilot", icon: Sparkles, id: "nav-copilot" },
  { to: "/data", label: "Data", icon: Upload, id: "nav-data" },
];

export const Shell = ({ plant, setPlant }) => {
  const [dark, setDark] = useState(true);
  const [plants, setPlants] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);

  useEffect(() => {
    api.get("/plants").then((r) => setPlants(r.data.plants)).catch(() => {});
  }, []);

  const reseed = async () => {
    await api.post("/seed/reset");
    toast.success("Demo dataset restored");
    navigate(0);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center gap-4">
            <NavLink to="/" data-testid="brand-home" className="flex items-center gap-2.5 shrink-0">
              <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary text-primary-foreground">
                <Boxes className="h-5 w-5" />
              </div>
              <div className="leading-tight hidden sm:block">
                <div className="font-display text-sm font-bold tracking-tight">Inventory Intelligence Copilot</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">MRO · Spare-parts optimisation</div>
              </div>
            </NavLink>
            <div className="ml-auto flex items-center gap-2">
              <Select value={plant} onValueChange={setPlant}>
                <SelectTrigger data-testid="plant-filter" className="h-9 w-[150px] font-mono text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="plant-option-all">All Plants</SelectItem>
                  {plants.map((p) => (
                    <SelectItem key={p} value={p} data-testid={`plant-option-${p}`}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" className="h-9 w-9" data-testid="reseed-btn" onClick={reseed} title="Reset demo data">
                <RotateCcw className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" className="h-9 w-9" data-testid="theme-toggle" onClick={() => setDark((d) => !d)}>
                {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          <nav className="flex items-center gap-1 overflow-x-auto pb-1">
            {NAV.map(({ to, label, icon: Icon, id }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                data-testid={id}
                className={({ isActive }) =>
                  `flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                    isActive ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>
      <main className="mx-auto max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
        <Outlet />
      </main>
    </div>
  );
};
